import {has, fullday, pickBy, createReducer, each, reject, isMatch, keys, difference, map, omit, omitBy, keyBy, mapValues, nodupes, isEmpty} from 'helpers';
import {repetitionValues, isPredefined} from 'store/activities';
import {isTemplate} from 'store/goals/selectors/type';

export const index = createReducer({}, {

  ACTIVITIES_UPDATE: (state, action) => {
    const activity = {
      //default values
      status: 'Not Started',
      //values to update
      ...state[action.activity.id],
      ...action.activity,
      id: action.activity.id || action.generateId('activityId'),
    }

    if (has(action.activity, 'duration')) {
      activity.allDay = activity.duration == fullday;
      if (activity.allDay) {
        activity.duration = activity.time = activity.times = null;
      }
    }

    if (action.activity.googleEventId && !action.activity.googleCalendarId) {
      activity.googleEventId = action.importedEvent = null;
    }

    state = {
      ...state,
      [activity.id]: activity,
    }

    if (action.subroutines) {
      //first remove all existing subroutines
      const { subroutines } = action.select('activities.details', activity) || {};
      state = omit(state, map(subroutines, 'id'));

      //re-add subroutines
      each(action.subroutines, (subroutine, index) => {
        state[subroutine.id || action.generateId('subroutineId' + index)] = {
          ...subroutine,
          ...repetitionValues(activity),
          dailyRoutineId: activity.id,
          orderIndex: index + 1,
        }
      });
    }

    return state;
  },

  ACTIVITIES_STATUS_UPDATE: (state, action) => {
    const activity = state[action.id];
    if (activity && !activity.repeatType) {
      return {
        ...state,
        [activity.id]: {
          ...state[activity.id],
          ...action.data,
        }
      }
    }
    return state;
  },

  ACTIVITIES_DUPLICATE: (state, action) => {
    const target = action.select('activities.activity', { id: action.referenceId });
    return {
      ...state,
      [action.generateId('activityId')]: {
        ...target,
        googleEventId: null,
        id: action.generateId('activityId'),
        name: nodupes({ names: { [target.name]: 1 } })(target.name),
        status: 'Not Started',
      }
    }
  },

  ACTIVITIES_RESCHEDULE: (state, action) => {
    const activity = { ...state[action.id] };
    if (!activity.repeatType) {
      if (action.date !== undefined) {
        activity.ts = action.date ? action.date.unix() : null;
      }
      if (action.time !== undefined) {
        activity.time = action.time;
        activity.allDay = false;
      }
      if (action.duration !== undefined) {
        activity.duration = action.duration;
        activity.allDay = activity.duration == fullday;
        if (activity.allDay) {
          activity.duration = activity.time = activity.times = null;
        }
      }
    }
    return {
      ...state,
      [action.id]: activity,
    }
  },

  ACTIVITIES_TOGGLE: (state, {id}) => {
    if (state[id]) {
      return {
        ...state,
        [id]: {
          ...state[id],
          status: state[id].status == 'Completed' ? 'Not Started' : 'Completed',
        }
      }
    }
    return state;
  },

  ACTIVITIES_HOLD: (state, action) => {
    const activity = state[action.id];
    return {
      ...state,
      [activity.id]: {
        ...activity,
        status: activity.status == 'On Hold' ? 'Not Started' : 'On Hold',
        scheduleDate: (activity.repeatType && activity.status == 'On Hold') ? action.timestamp_today : undefined,
      }
    }
  },

  ACTIVITIES_PLAN: (state, action) => {
    return {
      ...state,
      [action.id]: {
        ...state[action.id],
        ts: action.ts,
      }
    }
  },

  ACTIVITIES_UPDATE_PARENT: (state, action) => {
    if (state[action.source_id].goal_id != action.goal_id) {
      return {
        ...state,
        [action.source_id]: {
          ...state[action.source_id],
          goal_id: action.goal_id
        }
      }
    }
    return state;
  },

  ACTIVITIES_DELETE: (state, action) => {
    const { subroutines } = action.select('activities.details', { id: action.id }) || {};
    return omit(state, [ action.id ].concat(map(subroutines, 'id')));
  },

  CLARITY_LINK_GOAL_ACTIVITIES: (state, action) => {
    const goal = action.select('clarity.goals', action);
    for (const activity of action.select('clarity.activities', action).activities) {
      if (activity.goal_id != goal.id) {
        state = {
          ...state,
          [activity.id]: {
            ...activity,
            goal_id: goal.id,
          }
        }
      }
    }
    return state;
  },

  GOALS_DUPLICATE: (state, action) => {
    const duplicated = action.activities?.map(activity => ({
      ...activity,
      isTemplate: 0,
      status: 'Not Started',
      name: activity.name,
      note: activity.note,
      goal_id: (activity.goal_id === action.targetId || isEmpty(action.milestones)) ? action.duplicateId : action.generateId(`milestone${activity.goal_id}`),
      id: action.generateId(`activity${activity.id}`),
    }));
    if (duplicated.length) {
      return {
        ...state,
        ...keyBy(duplicated, 'id'),
      }
    }
    return state;
  },

  GOALS_DELETE: (state, action) => {
    if (action.allActivities?.length) {
      return {
        ...state,
        ...mapValues(keyBy(action.allActivities, 'id'), ({ id }) => omit(state[id], 'goal_id')),
      }
    }
    return state;
  },

  ACTIVITIES_STORE_GOOGLE_EVENT: (state, action) => {
    const googleEvent = action.select('activities.activity', { id: action.googleEventId });
    if (googleEvent) {
      return {
        ...state,
        [action.id]: {
          ...googleEvent,
          importedEvent: true,
          id: action.id,
        }
      }
    }
    return state;
  },

  GOOGLE_CALENDAR_SUCCESSFUL_CHANGE: (state, { change, event }) => {
    if (event && !event.recurringEventId && state[change.id] && state[change.id].googleEventId != event.googleEventId) {
      return {
        ...state,
        [change.id]: {
          ...state[change.id],
          googleEventId: event.googleEventId,
        }
      }
    }
    return state;
  },

  GOOGLE_CALENDAR_DATA_RECEIVED: (state, { events, select }) => {
    const stored = select('activities.storedGoogleEvents');
    for (const { id, importedEvent, status, ...event } of events) {
      if (stored[id] && status == 'Not Started') {
        const activity = {
          duration: '',
          time: '',
          ...stored[id],
        };
        if (!isMatch(activity, event)) {
          state = {
            ...state,
            [activity.id]: {
              ...stored[id],
              ...event,
            }
          }
        }
      } else if (stored[id]) {
        state = omit(state, stored[id].id);
      }
    }
    return state;
  },

  GOOGLE_CALENDAR_DELETE_IMPORTED_EVENTS: (state, { id }) => {
    if (id) {
      return omitBy(state, { importedEvent: true, googleCalendarId: id });
    }
    return omitBy(state, { importedEvent: true });
  },

  SERVER_DATA_RECEIVED: (state, action) => {
    const activities = reject(action.activities, isPredefined);
    const removed = difference(keys(state), action.existingElementIds.activities);
    if (activities.length || removed.length) {
      return omit({
        ...state,
        ...keyBy(activities, 'id'),
      }, removed);
    }
    return state;
  },

  TESTING_DELETE_ALL: () => ({})

});

